import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api, { getApiErrorMessage } from '../api';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') || '';
  const navigate = useNavigate();
  
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Timer effect
  React.useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      await api.post('/auth/verify-email', { email, code });
      setMessage('Email успешно подтвержден! Перенаправление на страницу входа...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка при подтверждении email. Проверьте код и количество попыток.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || isResending) return;
    
    setIsResending(true);
    setError('');
    setMessage('');

    try {
      await api.post('/auth/resend-verification', { email });
      setMessage('Новый код отправлен на вашу почту.');
      setResendTimer(60); // 60 seconds cooldown
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка при повторной отправке кода.'));
    } finally {
      setIsResending(true); // Keep loading state until timer starts? No, just end loading.
      setIsResending(false);
    }
  };

  return (
    <div className="login-container" style={styles.container}>
      <div className="login-card" style={styles.card}>
        <h2 style={styles.title}>Подтверждение Email</h2>
        <p style={styles.subtitle}>Введите 6-значный код, отправленный на вашу почту</p>
        
        {message && <div style={styles.successText}>{message}</div>}
        {error && <div style={styles.errorText}>{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input 
              type="email" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              required 
              style={styles.input}
              placeholder="vash@email.com"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Код подтверждения</label>
            <input 
              type="text" 
              value={code} 
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required 
              autoFocus
              style={{...styles.input, textAlign: 'center', fontSize: '24px', letterSpacing: '8px'}}
              placeholder="000000"
              maxLength="6"
            />
          </div>

          <button type="submit" disabled={isLoading || code.length < 6} style={styles.buttonSubmit}>
            {isLoading ? 'Загрузка...' : 'Подтвердить'}
          </button>

          <button 
            type="button" 
            onClick={handleResend} 
            disabled={resendTimer > 0 || isResending} 
            style={{...styles.buttonResend, opacity: (resendTimer > 0 || isResending) ? 0.6 : 1}}
          >
            {isResending ? 'Отправка...' : (resendTimer > 0 ? `Выслать повторно (${resendTimer}с)` : 'Выслать код повторно')}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Link to="/login" style={styles.fpLink}>Вернуться ко входу</Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f7fa',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    card: {
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
        textAlign: 'center',
        maxWidth: '400px',
        width: '90%'
    },
    title: {
        margin: '0 0 10px 0',
        color: '#2d3748',
        fontSize: '24px'
    },
    subtitle: {
        margin: '0 0 30px 0',
        color: '#718096',
        fontSize: '16px'
    },
    formGroup: {
        marginBottom: '20px',
        textAlign: 'left'
    },
    label: {
        display: 'block',
        marginBottom: '5px',
        fontWeight: 'bold',
        color: '#4a5568'
    },
    input: {
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #cbd5e0',
        boxSizing: 'border-box',
        fontSize: '16px'
    },
    buttonSubmit: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#3182ce',
        color: 'white',
        fontSize: '16px',
        fontWeight: 'bold',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background 0.2s',
        outline: 'none',
        marginBottom: '10px'
    },
    buttonResend: {
        width: '100%',
        padding: '10px',
        backgroundColor: 'transparent',
        color: '#3182ce',
        fontSize: '14px',
        fontWeight: '500',
        border: '1px solid #3182ce',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        outline: 'none'
    },
    errorText: {
        color: '#e53e3e',
        marginBottom: '15px',
        padding: '10px',
        backgroundColor: '#fff5f5',
        borderRadius: '6px',
        border: '1px solid #feb2b2',
        fontSize: '14px'
    },
    successText: {
        color: '#38a169',
        marginBottom: '15px',
        padding: '10px',
        backgroundColor: '#f0fff4',
        borderRadius: '6px',
        border: '1px solid #c6f6d5',
        fontSize: '14px'
    },
    fpLink: {
        color: '#3182ce',
        textDecoration: 'none',
        fontSize: '14px'
    }
};

export default VerifyEmail;
